import { ReactNode } from "react";
import { fontSize, fontWeight, textColor, statCardVariants } from "@/theme";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: ReactNode;
  variant?: "primary" | "success" | "warning" | "info" | "danger" | "purple";
  className?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    label: string;
  };
}

interface StatGridProps {
  children: ReactNode;
  className?: string;
}

interface DashboardSectionProps {
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  children: ReactNode;
}

export function StatCard({
  title,
  value,
  icon,
  variant = "primary",
  className,
}: StatCardProps) {
  return (
    <div
      className={`${statCardVariants.card[variant]} border rounded-lg p-6 ${className || ""}`}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className={`${fontSize.md} ${fontWeight.medium} ${textColor.black}`}>{title}</p>
          <p className={`${fontSize['3xl']} ${fontWeight.bold} ${textColor.heading} mt-2`}>{value}</p>
        </div>
        <div className={`${statCardVariants.icon[variant]} opacity-20`}>{icon}</div>
      </div>
    </div>
  );
}

export function StatGrid({ children, className }: StatGridProps) {
  return (
    <div
      className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${className || ""}`}
    >
      {children}
    </div>
  );
}

export function DashboardSection({
  title,
  description,
  actionLabel,
  onAction,
  children,
}: DashboardSectionProps) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className={`${fontSize.lg} ${fontWeight.bold} ${textColor.heading}`}>{title}</h3>
          {description && (
            <p className={`${fontSize.sm} ${textColor.muted} mt-1`}>{description}</p>
          )}
        </div>
        {actionLabel && onAction && (
          <button
            onClick={onAction}
            className={`${textColor.primary} hover:text-brand-700 ${fontSize.sm} ${fontWeight.medium}`}
          >
            {actionLabel} →
          </button>
        )}
      </div>
      {children}
    </div>
  );
}
